import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon, Button, ChildAvatar } from "../components/ui";
import DateField from "../components/DateField";
import { FORM_ASIDE } from "../data/constants";
import { familyService } from "../services/familyService";
import { useFamilyAuth } from "../store/familyAuthStore";
import { childAge, formatPhone, isValidPhone, PHONE_DIGITS } from "../utils/format";
import { validateRequiredForm } from "../utils/validate";

/* Convert any stored date into a yyyy-mm-dd value a <input type="date"> accepts. */
const toDateInput = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  const d = new Date(v);
  if (isNaN(d)) return "";
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

/* Resolvers used by each form's `prefill` map to copy the selected child's and
   the logged-in family's details into the matching fields. */
const PREFILL_SOURCES = {
  child_name: (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim(),
  child_room: (c) => c.room || "",
  child_dob: (c) => toDateInput(c.dob),
  family_name: (c, f) => f?.full_name || c?.family_name || "",
  family_email: (c, f) => f?.email || "",
  family_phone: (c, f) => f?.phone || "",
};

/* Field definitions per form code. Each form is an ordered list of blocks that
   mirror the brand form designs. Blocks: field | row | radio | childrows |
   file | section | note. The payload is stored as flexible JSON on the backend.
   `prefill` maps a field name to a PREFILL_SOURCES key, filled when a child is
   picked. */
const FORM_SCHEMA = {
  holiday: {
    childPicker: true,
    multiChild: false,
    dayRange: { from: "first_day", to: "last_day" },
    blocks: [
      { kind: "radio", name: "application_for", label: "Application for", required: true, layout: "grid",
        options: ["Holiday Discount", "Public Holiday Replacement Day"] },
      { kind: "section", label: "Holiday Notification" },
      { kind: "note", text: "My child will not attend care on the following dates. I acknowledge that 2 weeks notice is required for this notification." },
      { kind: "row", fields: [
        { name: "first_day", type: "date", label: "First day of leave", required: true },
        { name: "last_day", type: "date", label: "Last day of leave", required: true },
      ] },
    ],
  },

  sick: {
    childPicker: true,
    blocks: [
      { kind: "radio", name: "application_for", label: "Application for", required: true,
        options: ["Sick Day Replacement"] },
      { kind: "section", label: "Absentee Notification" },
      { kind: "note", text: "My child will not attend / did not attend care on the following date(s)." },
      { kind: "field", field: { name: "day_of_leave", type: "date", label: "Day of Leave", required: true } },
      { kind: "field", field: { name: "start_date", type: "date", label: "Start date with Centre", required: true } },
      { kind: "note", text: "Please provide 4 dates that are suitable (this needs to be within 10 days of the sick day)." },
      { kind: "row", fields: [
        { name: "date_1", type: "date", label: "Date 1" },
        { name: "date_2", type: "date", label: "Date 2" },
        { name: "date_3", type: "date", label: "Date 3" },
        { name: "date_4", type: "date", label: "Date 4" },
      ] },
    ],
  },

  change: {
    childPicker: true,
    blocks: [
      { kind: "radio", name: "booking_type", label: "Booking Type", options: ["Weekly", "Fortnightly"] },
      { kind: "field", field: { name: "new_booking_start", type: "date", label: "New booking to start" } },
      { kind: "note", text: "Need extra days sooner? If you require additional care within the two-week notice period, please book via the Xplor Home App. These requests will be prioritised." },
    ],
  },

  refund: {
    childPicker: true,
    blocks: [
      { kind: "field", field: { name: "date", type: "date", label: "Date" } },
      { kind: "radio", name: "reason", label: "Reason for Request", required: true,
        options: ["End of care", "Account incorrect", "Other"] },
      { kind: "field", field: { name: "amount", type: "number", label: "Total Amount to be refunded", placeholder: "$", required: true } },
      { kind: "file", name: "statement", label: "Please attach a copy of the most current statement", hint: "Max. file size: 512 MB." },
      { kind: "section", label: "Bank Account Details" },
      { kind: "field", field: { name: "bank_account_name", type: "text", label: "Bank Account Name", placeholder: "Bank Account Name:", required: true } },
      { kind: "row", fields: [
        { name: "bsb", type: "text", label: "BSB", placeholder: "BSB", required: true, hint: "must be 6 numbers" },
        { name: "account_number", type: "text", label: "Account Number", placeholder: "Account Number", required: true },
      ] },
      { kind: "note", text: "Payments will be generated after a two-week waiting period and will reflect amounts outstanding after CCS payment reconciliations are confirmed. All refunds will be processed via Direct Deposit." },
    ],
  },

  refer: {
    childPicker: false,
    blocks: [
      { kind: "section", label: "Your friend's details" },
      { kind: "field", field: { name: "friend_name", type: "text", label: "Full Name", placeholder: "Full Name", required: true } },
      { kind: "field", field: { name: "friend_email", type: "email", label: "Email Address", placeholder: "Email Address", required: true } },
      { kind: "field", field: { name: "friend_phone", type: "tel", label: "Phone Number", placeholder: "Phone Number" } },
    ],
  },

  end: {
    childPicker: true,
    blocks: [
      { kind: "field", field: { name: "last_day", type: "date", label: "Last day at the centre", required: true } },
    ],
  },
};

export default function FormOpen() {
  const { code } = useParams();
  const navigate = useNavigate();
  const family = useFamilyAuth((s) => s.family);
  const [forms, setForms] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const childId = selectedIds[0] || null;
  const [values, setValues] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    familyService.getForms().then((r) => setForms(r?.data || [])).catch(() => {});
    familyService.getChildren().then((r) => {
      const list = r?.data || [];
      setChildren(list);
      if (list[0]) setSelectedIds([list[0].id]);
    }).catch(() => {});
  }, []);

  const form = forms.find((f) => f.code === code);
  const schema = useMemo(
    () => FORM_SCHEMA[code] || { childPicker: true, blocks: [] },
    [code]
  );

  /* "Good to know" right-rail content comes from the form record (admin-editable).
     Falls back to the bundled copy if the backend hasn't been migrated yet. */
  const aside = useMemo(() => {
    if (form && form.aside_title) {
      let rules = form.aside_rules;
      if (typeof rules === "string") {
        try { rules = JSON.parse(rules); } catch { rules = []; }
      }
      return {
        title: form.aside_title,
        rules: Array.isArray(rules) ? rules : [],
        link: form.aside_link || null,
        showBalance: !!form.show_balance,
      };
    }
    return FORM_ASIDE[code] || null;
  }, [form, code]);

  /* Live holiday-absence balance for the selected child (only when this form's
     aside shows the balance card). Recomputed whenever the child changes or a
     submission is sent. */
  useEffect(() => {
    if (!aside?.showBalance) return;
    let active = true;
    familyService.getHolidayBalance(childId)
      .then((r) => { if (active) setBalance(r?.data || null); })
      .catch(() => { if (active) setBalance(null); });
    return () => { active = false; };
  }, [aside, childId, sent]);

  /* When child(ren) are picked, prefill their + the family's details into the
     matching fields. Supports multiple children: name fields are joined, and
     forms with a child/room table get one row filled per selected child. */
  useEffect(() => {
    const ids = schema.multiChild ? selectedIds : selectedIds.slice(0, 1);
    const sel = ids.map((id) => children.find((c) => c.id === id)).filter(Boolean);
    if (sel.length === 0) return;
    const fullName = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim();
    setValues((v) => {
      const next = { ...v };

      if (schema.childRows) {
        const { count, name, room } = schema.childRows;
        for (let r = 0; r < count; r++) {
          const c = sel[r];
          next[`${name}${r}`] = c ? fullName(c) : "";
          next[`${room}${r}`] = c ? (c.room || "") : "";
        }
        for (let r = count; r < 8; r++) {
          delete next[`${name}${r}`];
          delete next[`${room}${r}`];
        }
      }

      if (schema.prefill) {
        Object.entries(schema.prefill).forEach(([target, src]) => {
          if (src === "child_name") next[target] = sel.map(fullName).join(", ");
          else if (src === "child_room") next[target] = sel.map((c) => c.room).filter(Boolean).join(", ");
          else if (src === "child_dob") next[target] = toDateInput(sel[0].dob);
          else {
            const fn = PREFILL_SOURCES[src];
            if (fn) next[target] = fn(sel[0], family);
          }
        });
      }
      return next;
    });
  }, [selectedIds, children, family, schema]);

  /* Auto-select when a radio group has only one option (e.g. Sick Day form). */
  useEffect(() => {
    setValues((v) => {
      const next = { ...v };
      let changed = false;
      (schema.blocks || []).forEach((b) => {
        if (b.kind === "radio" && b.options?.length === 1 && next[b.name] !== b.options[0]) {
          next[b.name] = b.options[0];
          changed = true;
        }
      });
      return changed ? next : v;
    });
  }, [code, schema.blocks]);

  const days = useMemo(() => {
    const r = schema.dayRange;
    if (!r || !values[r.from] || !values[r.to]) return 0;
    const d = (new Date(values[r.to]) - new Date(values[r.from])) / 86400000 + 1;
    return d > 0 ? d : 0;
  }, [schema.dayRange, values]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const collectTelFields = useMemo(() => {
    const fields = [];
    (schema.blocks || []).forEach((b) => {
      if (b.kind === "field" && b.field?.type === "tel") fields.push(b.field);
      if (b.kind === "row") {
        b.fields.filter((f) => f.type === "tel").forEach((f) => fields.push(f));
      }
    });
    return fields;
  }, [schema.blocks]);

  const toggleChild = (id) => {
    setSelectedIds((prev) => {
      if (!schema.multiChild) return [id];
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        return next.length ? next : prev; // keep at least one selected
      }
      return [...prev, id];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateRequiredForm(e.currentTarget)) return;

    for (const f of collectTelFields) {
      const el = e.currentTarget.elements.namedItem(f.name);
      if (el && !el.validity.customError) {
        const raw = values[f.name] || "";
        const needsCheck = f.required || String(raw).trim();
        if (needsCheck && !isValidPhone(raw, { required: !!f.required })) {
          el.setCustomValidity(
            f.required
              ? `Enter ${f.label.toLowerCase()} (${PHONE_DIGITS} digits)`
              : `${f.label} must be ${PHONE_DIGITS} digits if provided`
          );
          el.classList.add("input--error");
          el.focus();
          setError(
            f.required
              ? `Please enter a valid ${f.label.toLowerCase()} (${PHONE_DIGITS} digits).`
              : `${f.label} must be ${PHONE_DIGITS} digits if provided.`
          );
          return;
        }
      }
    }

    setBusy(true);
    try {
      const payload = { ...values, note };
      const res = await familyService.submitForm({
        form_code: code,
        child_id: schema.childPicker ? childId : null,
        payload,
      });
      if (res?.success) setSent(res.data);
      else setError(res?.msg || "We couldn't send this form.");
    } catch (err) {
      setError(err?.response?.data?.msg || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const renderField = (f) => {
    return (
      <div key={f.name} className="form-block">
        <label className="field-label">{f.label}{f.required && <span className="req">*</span>}</label>
        {f.type === "date" ? (
          <DateField name={f.name} value={values[f.name] || ""} onChange={(v) => set(f.name, v)} required={f.required} />
        ) : (
          <input className="input" name={f.name} type={f.type || "text"} placeholder={f.placeholder || ""}
            value={values[f.name] || ""}
            onChange={(e) => set(f.name, f.type === "tel" ? formatPhone(e.target.value) : e.target.value)}
            required={f.required}
            {...(f.type === "tel" ? { inputMode: "numeric" } : {})}
            {...(f.type === "number" ? { min: "0", step: "0.01" } : {})} />
        )}
        {f.hint && <p className="field-hint">{f.hint}</p>}
      </div>
    );
  };

  const renderBlock = (b, i) => {
    switch (b.kind) {
      case "section":
        return <h2 key={"sec" + i} className="form-section">{b.label}</h2>;
      case "note":
        return <p key={"note" + i} className="form-note">{b.text}</p>;
      case "field":
        return renderField(b.field);
      case "row":
        return (
          <div key={"row" + i} className="field-row form-block">
            {b.fields.map((f) => (
              <div key={f.name}>
                <label className="field-label">{f.label}{f.required && <span className="req">*</span>}</label>
                {f.type === "date" ? (
                  <DateField name={f.name} value={values[f.name] || ""} onChange={(v) => set(f.name, v)} required={f.required} />
                ) : (
                  <input className="input" name={f.name} type={f.type || "text"} placeholder={f.placeholder || ""}
                    value={values[f.name] || ""}
                    onChange={(e) => set(f.name, f.type === "tel" ? formatPhone(e.target.value) : e.target.value)}
                    required={f.required}
                    {...(f.type === "tel" ? { inputMode: "numeric" } : {})}
                    {...(f.type === "number" ? { min: "0", step: "0.01" } : {})} />
                )}
                {f.hint && <p className="field-hint">{f.hint}</p>}
              </div>
            ))}
          </div>
        );
      case "radio": {
        const sole = b.options?.length === 1 ? b.options[0] : null;
        if (sole) {
          return (
            <div key={"radio" + i} className="form-block">
              <label className="field-label" id={`${b.name}-label`}>
                {b.label}{b.required && <span className="req">*</span>}
              </label>
              <div className="form-single-choice" aria-labelledby={`${b.name}-label`}>
                <input type="hidden" name={b.name} value={sole} />
                <span className="form-single-choice__value">{sole}</span>
              </div>
            </div>
          );
        }
        return (
          <div key={"radio" + i} className="form-block">
            <label className="field-label" id={`${b.name}-label`}>
              {b.label}{b.required && <span className="req">*</span>}
            </label>
            {b.layout === "grid" ? (
              <div className="radio-matrix" role="radiogroup" aria-labelledby={`${b.name}-label`}>
                {b.options.map((o, idx) => (
                  <label key={o} className="radio-matrix-item">
                    <input
                      type="radio"
                      name={b.name}
                      value={o}
                      checked={values[b.name] === o}
                      onChange={() => set(b.name, o)}
                      required={b.required && idx === 0 && !values[b.name]}
                    />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="radio-group" role="radiogroup" aria-labelledby={`${b.name}-label`}>
                {b.options.map((o, idx) => (
                  <label key={o} className="radio-opt">
                    <input
                      type="radio"
                      name={b.name}
                      value={o}
                      checked={values[b.name] === o}
                      onChange={() => set(b.name, o)}
                      required={b.required && idx === 0 && !values[b.name]}
                    />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      }
      case "file":
        return (
          <div key={"file" + i} className="form-block">
            <label className="field-label">{b.label}</label>
            <input className="file-input" type="file"
              onChange={(e) => set(b.name, e.target.files?.[0]?.name || "")} />
            {b.hint && <p className="field-hint">{b.hint}</p>}
          </div>
        );
      case "childrows":
        return (
          <div key={"crows" + i} className="form-block child-rows">
            <div className="field-row child-rows-head">
              <div><label className="field-label">{b.label}<span className="req">*</span></label></div>
              <div><label className="field-label">Room</label></div>
            </div>
            {Array.from({ length: b.count || 1 }).map((_, r) => (
              <div key={r} className="field-row child-row">
                <input className="input" name={`child_name_${r}`} type="text"
                  placeholder={b.count === 1 ? "Child's name" : "Child/ren's Name(s)"}
                  value={values[`child_name_${r}`] || ""}
                  onChange={(e) => set(`child_name_${r}`, e.target.value)}
                  required={r === 0} />
                <input className="input" name={`room_${r}`} type="text" placeholder="Room"
                  value={values[`room_${r}`] || ""}
                  onChange={(e) => set(`room_${r}`, e.target.value)} />
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  if (sent) {
    const childName = children.find((c) => c.id === childId)?.first_name;
    return (
      <div className="form-sent fade-in">
        <span className="icon-circle lg xl"><Icon name="check" className="ico-34" /></span>
        <h1 className="mt-24">Sent to your centre</h1>
        <p className="form-sent-lead">
          Thanks — your {(form?.name || "form").toLowerCase()}{schema.childPicker && childName ? ` for ${childName}` : ""} has been sent.
          You'll get a confirmation once it's reviewed.
        </p>
        <div className="ref-pill ref-pill--lg">Reference: {sent.reference_no}</div>
        <div className="form-sent-actions">
          <Button variant="primary" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-open">
      <div className="form-open-main scroll-area">
        <div className="form-open-inner">
          <button className="back-link" onClick={() => navigate("/forms")}>
            <Icon name="arrowLeft" className="ico-16" /> All forms
          </button>

          <span className="icon-circle lg mt-8"><Icon name={form?.icon || "forms"} /></span>
          <h1 className="mt-18">{form?.name || "Form"}</h1>
          <p className="form-lead">{form?.description}</p>

          {error && <div className="alert-soft mt-20">{error}</div>}

          <form onSubmit={submit} className="form-open-form" noValidate>
            {schema.childPicker && children.length > 0 && (
              <div className="form-block child-picker">
                <label className="field-label">
                  {schema.multiChild ? "Which children is this for?" : "Which child is this for?"}
                  {schema.multiChild && <span className="optional"> (select one or more)</span>}
                </label>
                <div className="child-select">
                  {children.map((c, i) => {
                    const on = selectedIds.includes(c.id);
                    const soleChild = children.length === 1;
                    const body = (
                      <>
                        <ChildAvatar c={{ name: c.first_name, index: i }} size={40} />
                        <span>
                          <span className="co-name">{c.first_name}</span>
                          <span className="co-meta">{[childAge(c.dob), c.room].filter(Boolean).join(" · ")}</span>
                        </span>
                        <span className={"co-tick" + (soleChild || on ? " on" : "")}>
                          <Icon name="check" className="ico-13" />
                        </span>
                      </>
                    );
                    if (soleChild) {
                      return (
                        <div key={c.id} className="child-opt is-active">
                          {body}
                        </div>
                      );
                    }
                    return (
                      <button type="button" key={c.id}
                        className={"child-opt" + (on ? " is-active" : "")}
                        onClick={() => toggleChild(c.id)}>
                        {body}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {schema.blocks.map((b, i) => renderBlock(b, i))}

            {days > 0 && (
              <p className="day-count"><Icon name="calendar" className="ico-15" /> {days} day{days > 1 ? "s" : ""} away from your booked schedule.</p>
            )}

            <div className="form-block">
              <label className="field-label">Anything we should know? <span className="optional">(optional)</span></label>
              <textarea className="textarea" placeholder="A short note for your centre." value={note} onChange={(e) => setNote(e.target.value)} />
            </div>

            <div className="form-submit-bar">
              <Button variant="primary" type="submit" disabled={busy} iconRight={busy ? null : "chevronRight"}>
                {busy ? "Sending…" : (code === "refer" ? "Send referral" : "Save and send to your centre")}
              </Button>
              <button type="button" className="link-btn" onClick={() => navigate("/forms")}>Cancel</button>
            </div>
          </form>
        </div>
      </div>

      {aside && (
        <aside className="form-aside scroll-area">
          {aside.showBalance && (
            <div className="aside-card">
              <p className="eyebrow mb-14">Holiday balance</p>
              {balance ? (
                <>
                  <div className="balance">
                    <div className="balance-num">{balance.remaining}<span>/{balance.allowance}</span></div>
                    <div className="balance-label">allowable absence days remaining this year</div>
                  </div>
                  <div className="balance-bar">
                    <span style={{ width: `${(balance.remaining / balance.allowance) * 100}%` }} />
                  </div>
                </>
              ) : (
                <div className="balance-label">Loading balance…</div>
              )}
            </div>
          )}
          <div className="aside-card">
            <p className="eyebrow mb-14">{aside.title}</p>
            <ul className="rule-list">
              {aside.rules.map((r, i) => (
                <li key={i}><span className="ri"><Icon name="check" className="ico-13" /></span><div>{r}</div></li>
              ))}
            </ul>
            {aside.link && (
              <a href="/policies" onClick={(e) => { e.preventDefault(); navigate("/policies"); }} className="aside-link">
                {aside.link} <Icon name="external" className="ico-13" />
              </a>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
